<template>
    <div>
        <div class="relative w-full">
            <form>
                <label for="voice-search" class="sr-only">Search integrations</label>
                <input ref="searchInput" type="text" id="voice-search"
                    class="search-bar text-sm rounded-2xl w-full p-2.5" required v-model="search" />
                <button type="button" class="absolute inset-0 w-full search-btn" aria-label="Search">
                    <svg aria-hidden="true" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd"
                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                            clip-rule="evenodd"></path>
                    </svg>
                </button>
                <!-- <button type="button" class="absolute inset-y-0 right-0 flex items-center pr-3">
                    <kbd v-if="platform === 'mac'" class="text-[var(--medium-gray)] font-sans hover:text-[var(--medium-gray)]">⌘k</kbd>
                    <kbd v-if="platform === 'mac'" class="text-[var(--medium-gray)] font-sans">k</kbd>
                    <kbd v-else class="text-[var(--medium-gray)] font-sans">/</kbd>
                </button> -->
            </form>

            <div v-if="filteredItems.length"
                class="whitespace-nowrap sm:absolute inline-block relative sm:left-0 sm:top-10 sm:mt-2 py-2 bg-[var(--dark-gray)] rounded sm:shadow-md text-sm xs:shadow-none border">
                <a v-for="item in filteredItems" :key="item.name" :href="item.link"
                    class="block px-8 py-2 text-medium-gray hover:bg-[var(--light-gray)] hover:text-[var(--dark-gray)] cursor-pointer">{{
                        item.name }}</a>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { INTEGRATIONS } from '../services/integrations';
import { CATEGORY_PAGE_SLUGS, MAP_CATS, META_CATEGORIES } from '~~/services/categories';
import { type IMenuItem } from '~~/services/common';

type KeyListenFunction = {
    (e: KeyboardEvent): void;
};

export default {
    beforeUnmount() {
        this.removeKeyListener();
    },
    mounted() {
        const platform = (window.navigator.platform || navigator?.userAgent || '').toLowerCase();
        this.platform = platform?.includes('windows') ? 'windows' : platform?.includes('mac') ? 'mac' : 'unix';
        this.load();
        this.addKeyListener();
    },
    watch: {
        search(value: string) {
            if (!value) {
                this.filteredItems = [];
                return;
            }
            // console.log('search', value, this.search);
            value = value.toLowerCase();
            this.filteredItems = this.items.filter((i) => i.name.toLowerCase().includes(value));
            // console.log('length', this.filteredItems.length);
            this.filteredItems = this.filteredItems.slice(0, 8);
        },
    },
    methods: {
        addKeyListener() {
            this.keyListener = function (e: KeyboardEvent) {
                if (e && e.key === 'k' && (e.ctrlKey || e.metaKey)) {
                    const theField = <HTMLInputElement>this.$refs.searchInput;
                    if (theField) {
                        theField.focus();
                        return e.preventDefault();
                    }
                }

                if (e && e.key === '/') {
                    const theField = <HTMLInputElement>this.$refs.searchInput;
                    if (theField) {
                        theField.focus();
                        return e.preventDefault();
                    }
                }
            };

            document.addEventListener('keydown', this.keyListener.bind(this));
        },
        removeKeyListener() {
            document.removeEventListener('keydown', this.keyListener);
        },
        load() {
            Object.entries(META_CATEGORIES).forEach(([slug, meta]) => {
                this.items.push({
                    name: `${meta.name} integrations`,
                    link: `/${slug}`,
                });
            });
            CATEGORY_PAGE_SLUGS.forEach((c) => {
                this.items.push({
                    name: `${MAP_CATS[c]} integrations`,
                    link: `/${c}`,
                });
            });

            INTEGRATIONS.forEach((i) => {
                this.items.push({
                    name: `${i.name} integration`,
                    link: `/integrations/${i.type}`,
                });
            });
        },
    },
    data() {
        return {
            platform: 'mac',
            search: '',

            keyListener: ((e: KeyboardEvent) => {
                // console.log(e);
            }) as KeyListenFunction,

            items: [
                {
                    name: 'LinkedIn Company Page',
                    link: 'https://www.linkedin.com/company/unified-to/',
                },
                {
                    name: 'G2 Reviews',
                    link: 'https://www.g2.com/products/unified-to/reviews',
                },
                {
                    name: 'About Unified',
                    link: '/about',
                },
                {
                    name: 'Security Certifications',
                    link: 'https://app.mycroft.io/trust/unified-to',
                },
                {
                    name: 'Trust Center',
                    link: 'https://app.mycroft.io/trust/unified-to',
                },
                {
                    name: 'Changelog',
                    link: '/changelog',
                },
                {
                    name: 'Case Studies',
                    link: '/casestudies',
                },
                {
                    name: 'Authentication',
                    link: '/embeddedauth',
                },
                {
                    name: 'Authorization',
                    link: '/embeddedauth',
                },
                {
                    name: 'Single Sign-On',
                    link: '/login',
                },
                {
                    name: 'sso',
                    link: '/login',
                },
                {
                    name: 'login',
                    link: '/login',
                },
                {
                    name: 'MCP',
                    link: '/mcp',
                },
                {
                    name: 'Technology',
                    link: '/technology',
                },
                {
                    name: 'Security',
                    link: '/security',
                },
                {
                    name: 'API Documentations',
                    link: 'https://docs.unified.to',
                },
                {
                    name: 'Pricing',
                    link: '/pricing',
                },
                {
                    name: 'Contact',
                    link: '/contact',
                },
                {
                    name: 'Blog',
                    link: 'https://unified.to/blog',
                },
                {
                    name: 'Privacy Policy',
                    link: '/privacy',
                },
                {
                    name: 'Terms of Usage',
                    link: '/tos',
                },
                {
                    name: 'Uptime status',
                    link: 'https://status.unified.to',
                },
            ] as IMenuItem[],
            filteredItems: [] as IMenuItem[],
        };
    },
};
</script>

<style scoped>
.form {
    transform: translate(-50%, -50%);
    transition: all 1s;

}

input {
    transition: all 1s;
    opacity: 0;
    width: 40px;
}

.search-btn {
    transition: all 1s;
    top: 0;
    transform: translatex(25%);
    color: white;
}

form:hover,
form:valid {
    cursor: pointer;
}

form:hover input,
form:valid input {
    display: block;
    opacity: 1;
    width: 200px;
}

form:hover .search-btn,
form:valid .search-btn {
    right: 0;
    transform: translatex(13em);
    color: gray;
}

form:valid button {
    display: block;
}
</style>